if (!customElements.get('reviews-slider')) {
  class ReviewsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rs-track]');
      if (!this.track) return;

      this.section = this.closest('.reviews-slider');
      this.prevButton = this.section.querySelector('[data-rs-prev]');
      this.nextButton = this.section.querySelector('[data-rs-next]');
      this.dotsContainer = this.querySelector('[data-rs-dots]');

      this.loop = this.dataset.loop === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      this.autoplayTimer = null;
      this.dots = [];

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      };
      this.onScroll = () => {
        window.clearTimeout(this.scrollTimeout);
        this.scrollTimeout = window.setTimeout(() => {
          this.updateArrows();
          this.updateDots();
        }, 100);
      };
      this.onResize = () => {
        window.clearTimeout(this.resizeTimeout);
        this.resizeTimeout = window.setTimeout(() => {
          this.buildDots();
          this.updateArrows();
        }, 150);
      };

      this.prevButton && this.prevButton.addEventListener('click', this.onPrev);
      this.nextButton && this.nextButton.addEventListener('click', this.onNext);
      this.track.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.setupDrag();
      this.buildDots();
      requestAnimationFrame(() => {
        this.updateArrows();
        this.updateDots();
      });

      if (this.autoplay) {
        this.startAutoplay();
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('pointerdown', () => this.stopAutoplay());
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
    }

    getVisibleCount() {
      const value = getComputedStyle(this.section).getPropertyValue('--rs-active-cards');
      const parsed = Math.round(parseFloat(value));
      return parsed > 0 ? parsed : 1;
    }

    getStep() {
      const slide = this.track.querySelector('.reviews-slider__card');
      if (!slide) return 0;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    step(direction) {
      const step = this.getStep();
      if (!step) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const atEnd = this.track.scrollLeft >= maxScroll - 1;
      const atStart = this.track.scrollLeft <= 1;

      if (this.loop && direction === 1 && atEnd) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (this.loop && direction === -1 && atStart) {
        this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }

      this.track.scrollBy({ left: step * direction * this.getVisibleCount(), behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.prevButton && !this.nextButton) return;

      if (this.loop) {
        if (this.prevButton) this.prevButton.disabled = false;
        if (this.nextButton) this.nextButton.disabled = false;
        return;
      }

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
    }

    buildDots() {
      if (!this.dotsContainer) return;

      const total = this.track.querySelectorAll('.reviews-slider__card').length;
      const perView = this.getVisibleCount();
      const pages = Math.max(1, Math.ceil(total / perView));

      this.dotsContainer.innerHTML = '';
      this.dots = [];

      if (pages <= 1) return;

      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'reviews-slider__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', () => this.goToPage(i));
        this.dotsContainer.appendChild(dot);
        this.dots.push(dot);
      }

      this.updateDots();
    }

    goToPage(index) {
      const step = this.getStep() * this.getVisibleCount();
      this.track.scrollTo({ left: step * index, behavior: 'smooth' });
    }

    updateDots() {
      if (!this.dots.length) return;
      const step = this.getStep() * this.getVisibleCount();
      const index = step ? Math.round(this.track.scrollLeft / step) : 0;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => this.step(1), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    setupDrag() {
      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      const pointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        isDown = true;
        moved = false;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        this.stopAutoplay();
      };

      const pointerMove = (event) => {
        if (!isDown) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 4) moved = true;
        this.track.scrollLeft = startScroll - delta;
      };

      const pointerUp = () => {
        if (!isDown) return;
        isDown = false;
        this.track.classList.remove('is-dragging');
        if (this.autoplay) this.startAutoplay();
      };

      this.track.addEventListener('pointerdown', pointerDown);
      this.track.addEventListener('pointermove', pointerMove);
      this.track.addEventListener('pointerup', pointerUp);
      this.track.addEventListener('pointerleave', pointerUp);
      this.track.addEventListener(
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );
    }
  }

  customElements.define('reviews-slider', ReviewsSlider);
}
